// ============================================================
// STORAGE LAYER — the app's persistence boundary.
//
// Backends, in priority order (one is chosen once at module evaluation):
//   1. http     — the @starpoint/api backend (Postgres + Prisma). Production;
//                 selected when window.__VITE_API_URL__ is set.
//   2. memory   — in-memory Map (tests / no backend configured). Loses data
//                 on refresh.
//
// There were three. The `supabase` backend — direct PostgREST against the
// `padel24_storage` table — is GONE (#241), after the cutover proved the http
// path in the deployed dev environment. It is removed rather than left unused
// because an unused backend is one empty build-arg away from becoming the live
// one, silently: nothing in the UI says which backend it is talking to. The
// old localStorage + window.storage (Claude-artifact) backends went earlier,
// in the Postgres migration — see docs/architecture/DATABASE.md.
//
// PUBLIC contract (unchanged — the rest of the app relies on it):
//   - get(key) → Promise<{value} | null>   (never throws; null when absent)
//   - set(key, value) → Promise<{value}>   (THROWS on failure — Storage-first)
//   - delete(key) → Promise<{deleted}>
//   - list(prefix) → Promise<{keys}>
// Callers JSON.stringify on write and JSON.parse(res.value) on read.
//
// The window.__VITE_*__ globals are injected by Vite `define` at build time and
// ambient-typed in ../types/window.d.ts.
// ============================================================
import { withoutParticipantContactValue } from '@starpoint/engine/eventDocumentContact';
import { apiFetch } from './api/http';

// Which backend was selected at module evaluation.
/**
 * Raised when the SERVER refused a conditional write because the stored value
 * moved since it was read (#245).
 */
export class StorageConflictError extends Error {
  readonly key: string;
  /** The version the row was actually at — for diagnostics, NOT for retrying. */
  readonly serverVersion: number | null;
  constructor(key: string, serverVersion: number | null = null) {
    super(`Lagringskonflikt: «${key}» ble endret av noen andre`);
    this.name = 'StorageConflictError';
    this.key = key;
    this.serverVersion = serverVersion;
  }
}

/**
 * Raised when the server refused the write because the document asserted a
 * value for a field it DOES NOT OWN (#766 slice B).
 *
 * Today there is exactly one: `club_info_v1.name`. The organization's name
 * belongs to `tenants.name` and changes only through the rename door, which
 * checks the capability and writes an audit row. A blob that carries a
 * different name is refused rather than stripped, because a silent strip is
 * how the two copies drifted apart in the first place.
 *
 * SEPARATE FROM StorageConflictError even though both arrive as a 409, because
 * the recovery is different in kind. A version conflict means «somebody else
 * saved first, rebuild and retry» and retrying can work. This means «that
 * field is not yours to write», and resending the same document never works
 * however fresh it is — the caller has to drop the field or go through the
 * other door. Telling a person to try again would be a lie.
 *
 * The key is still invalidated: this client's copy is demonstrably not the
 * server's, and the fresh read is what supplies the canonical value.
 */
export class StorageReadOnlyFieldError extends Error {
  readonly key: string;
  /** The machine code from the API. Never a sentence, never translated. */
  readonly code: string;
  /** The server's canonical value, when it sent one — so the UI can say it. */
  readonly canonical: string | null;
  constructor(key: string, code: string, canonical: string | null) {
    super(`«${key}» ble avvist: feltet eies av serveren (${code})`);
    this.name = 'StorageReadOnlyFieldError';
    this.key = key;
    this.code = code;
    this.canonical = canonical;
  }
}

/**
 * Raised LOCALLY, without contacting the server, because this client's copy of
 * the key is known to be out of date and must be re-read before it may be
 * written again.
 */
export class StorageStaleError extends Error {
  readonly key: string;
  constructor(key: string) {
    super(`Utdatert kopi av «${key}» — hent på nytt før du lagrer`);
    this.name = 'StorageStaleError';
    this.key = key;
  }
}

// ── optimistic-concurrency tokens, per key ────────────────────────────
//
// Held here rather than threaded through Storage.set's signature, which every
// feature in the app calls. The tracking is invisible to them: a read records
// the version, the next write presents it.
//
// THREE STATES, and the third one is the point.
//
//   absent        never read — written unconditionally (pre-#245 behaviour)
//   { version }   read at version N — the next write says "only if still N"
//   'must-refresh' this client's COPY is stale, and no write may go out until
//                 get() has refreshed it
//
// Why the third state has to exist. Adopting the server's version after a 409
// looks helpful and is the opposite: the write was refused because our
// DOCUMENT is stale, and the version is not what is stale about it. Recording
// the server's version would let the very next set() re-send the same
// out-of-date document with a precondition that now matches — turning a write
// the server correctly refused into one it accepts, and erasing whatever it
// was protecting. The refusal has to stick to the client's copy, not to a
// number.
//
// The ambiguous-failure case is the same problem mirrored. After a 500 or a
// dropped connection we do not know whether the write landed, so neither a
// remembered version nor "no precondition" is honest: one may match by luck,
// the other gives up the check entirely. Both let a stale document through.
//
// Nothing here merges, retries or adopts a version on the caller's behalf. The
// caller must re-read and rebuild; this only makes it impossible to skip.
type Token = { kind: 'version'; version: number } | { kind: 'must-refresh' };

const _tokens = new Map<string, Token>();

/** A read landed: the copy is fresh again, at a known version (absent ⇒ 0). */
const recordVersion = (key: string, version: unknown) => {
  if (typeof version === 'number' && Number.isInteger(version)) {
    _tokens.set(key, { kind: 'version', version });
  } else {
    // A 200 with no usable version: fresh value, unknown version. Refuse to
    // guess — a wrong precondition is worse than an honest refresh.
    _tokens.set(key, { kind: 'must-refresh' });
  }
};

/** This client's copy can no longer be trusted; block writes until re-read. */
const invalidate = (key: string) => _tokens.set(key, { kind: 'must-refresh' });

const tokenFor = (key: string): Token | undefined => _tokens.get(key);

/**
 * A SERVER COMMAND wrote this key, and handed back what it wrote.
 *
 * THE ONE LEGITIMATE WAY TO LEARN A VERSION WITHOUT CALLING `get`, and it is
 * not the adoption the doctrine above forbids. That one is dangerous because
 * the client's DOCUMENT is stale and a number cannot fix it. Here the client
 * is not stale at all: the command's response carries the document the server
 * just stored *and* the version it now has. That is a read, delivered over a
 * different route.
 *
 * Which is why `value` is a parameter and is not optional. It is not used —
 * the caller puts it into its own state — it is there so this cannot be called
 * with a bare number. A caller that does not hold the server's document has
 * nothing to adopt, and the signature says so.
 *
 * PASS `null` WHEN THAT IS THE CASE, and it happens for real: the result
 * command writes a PAUSED tournament into `paused_tournaments_v1`, whose value
 * is the whole LIST, and the response carries only the one session. The client
 * could rebuild the list by substitution and would be right almost always —
 * and «almost» is what a precondition exists to catch. So it says it does not
 * hold the document, the key is marked must-refresh, and the next write costs
 * one extra read instead of risking a version that matches a document nobody
 * checked.
 *
 * An unusable version does the same thing, exactly as a 200 with no version
 * does: refusing to guess is the whole point.
 */
export function adoptServerWrite(key: string, value: string | null, version: unknown): void {
  if (value === null) {
    invalidate(key);
    return;
  }
  recordVersion(key, version);
}

// ── ONE CALL AT A TIME PER KEY, from this tab (#1297) ────────────────────
//
// The token above is only as good as the order its answers arrive in. Two
// `set()` calls on one key, the second issued before the first answers, both
// read the SAME token, so both say «only if still N». The first lands and moves
// the key to N+1; the second is refused 409, the key goes to must-refresh, and
// whatever it carried is LOST. Nobody else wrote. Creating a tournament and
// pausing one each did exactly this, and once a whole tournament vanished.
//
// So the calls on one key run in order: each waits for the one before it to
// SETTLE (succeed or fail), and only then reads the token. A write then
// carries the version the previous write returned, and a refusal still means
// what it should — ANOTHER writer moved the key. Reads queue too: a read
// answered before an in-flight write commits, but processed after it, would
// otherwise record the old version and recreate the same self-conflict.
//
// It does not merge, retry or reorder anything. A write that follows a failed
// one meets the must-refresh that failure left, and is refused before it is
// sent, exactly as before. Different keys never wait for each other.
const _queues = new Map<string, Promise<unknown>>();

function inOrder<T>(key: string, run: () => Promise<T>): Promise<T> {
  const before = _queues.get(key) ?? Promise.resolve();
  const call = before.then(run, run);
  const settled = call.then(
    () => undefined,
    () => undefined,
  );
  _queues.set(key, settled);
  // Forget the key once nothing is queued behind this call, so the map cannot grow.
  void settled.then(() => {
    if (_queues.get(key) === settled) _queues.delete(key);
  });
  return call;
}

/** Tests only: drop the tokens so one case cannot inherit another's. */
export function __resetStorageVersions(): void {
  _tokens.clear();
  _queues.clear();
}

export type StorageBackend = 'http' | 'memory';

// The PUBLIC contract the rest of the app uses. get(key) returns { value }
// — NOT a raw string. Internally the backends are loosely typed (any) because
// the shapes vary per backend; we don't over-type during the migration.
/**
 * A read that says WHY it has no value (#413).
 *
 * `get` cannot: it answers `null` both for "this key does not exist" and for
 * "the request failed", and the mount loader has to tell those apart. Treating
 * a failed read as an empty club is how a live tournament appeared to vanish
 * during an API restart — the screen showed nothing, the blanket-persist flag
 * was armed anyway, and the operator's next action overwrote a document that
 * was still in the database.
 *
 * Deliberately a SECOND method rather than a change to `get`, which has 28
 * call sites that reasonably treat `null` as absent. Only a caller that must
 * distinguish should have to.
 */
export type StorageRead =
  | { status: 'ok'; value: string }
  /** The key genuinely does not exist. A fresh, trustworthy answer. */
  | { status: 'absent' }
  /** The request failed. This says NOTHING about what is stored. */
  | { status: 'failed' };

export interface StorageApi {
  readonly backend: StorageBackend;
  get(key: string): Promise<{ value: string } | null>;
  read(key: string): Promise<StorageRead>;
  set(key: string, value: string): Promise<{ value: string }>;
  delete(key: string): Promise<{ deleted: boolean }>;
  list(prefix?: string): Promise<{ keys: string[] }>;
}

/**
 * Whether a `GET /kv/:key` answer means «nothing is stored under this key».
 *
 * 204 is how the API says it since #1145: no value is an answer, not an error,
 * and a 404 painted every console red for keys that simply do not exist yet.
 * 404 STILL COUNTS, on purpose: it is what an API from before that change says,
 * and a browser and an API are not deployed in the same instant. Both are
 * fresh, trustworthy answers — unlike a 5xx or a network error, which say
 * nothing about what is stored.
 */
export const isAbsentAnswer = (status: number): boolean => status === 204 || status === 404;

/**
 * Appended to a `GET` that may find nothing: «I read 204 as no value».
 *
 * The API answers 204 ONLY to a request that says so, and 404 to every other
 * (`apps/api/src/lib/noValueAnswer.ts`). That is what keeps a tab still
 * running a bundle from before #1145 safe: it cannot read a 204, and handed
 * one it would record no version and make its next write a blind overwrite.
 * A query parameter rather than a header, so it needs no CORS preflight
 * change. The two spellings meet in `apps/api/tests/kv-no-value-boundary.test.ts`.
 */
export const NO_VALUE_OPT_IN = '?absent=204';

const _RawStorage = (() => {
  // The Vite build exposes env vars as global window variables via the define
  // config: `window.__VITE_API_URL__`. Read directly, not via dynamic lookup.
  // Vite's define replaces literal text, so we MUST write the name verbatim.
  // If Vite hasn't replaced it (Claude chat environment), we fall back to a typeof check.
  const getEnvApiUrl = () => {
    try {
      if (typeof window !== 'undefined' && window.__VITE_API_URL__) {
        return window.__VITE_API_URL__;
      }
    } catch {
      /* no window/env — fall back */
    }
    return '';
  };

  // Backend selection: http (VITE_API_URL) → memory.

  // 1. PRIORITY — HTTP API (@starpoint/api → Postgres + Prisma), if VITE_API_URL
  //    is configured. This is the production path, and since #241 the only
  //    real one: without it the app falls through to memory, which loses data
  //    on refresh and says so loudly in the console.
  //
  //    Contract parity is load-bearing (HANDOVER §13 Storage-first ordering):
  //    get() returns null on 204/404/error (never throws); set() THROWS on non-2xx
  //    so `await Storage.set(...)` before setState still rejects on failure.
  //
  //    Requests go through lib/api/http.ts, which attaches the signed-in staff
  //    member's bearer token when there is one. /kv is authenticated AND
  //    tenant-scoped server-side now, so an unauthenticated call answers 401 —
  //    which is why enabling VITE_API_URL is a deliberate later step and not
  //    something this backend can do on its own (docs/architecture/SECURITY.md §4e).
  const apiUrl = getEnvApiUrl();
  if (apiUrl) {
    const base = apiUrl.replace(/\/$/, '');
    console.log('[Padel24] Storage: http (' + base + ')');
    return {
      backend: 'http',

      read: (key: string): Promise<StorageRead> =>
        inOrder(key, async (): Promise<StorageRead> => {
          try {
            const res = await apiFetch('/kv/' + encodeURIComponent(key) + NO_VALUE_OPT_IN);
            if (isAbsentAnswer(res.status)) {
              recordVersion(key, 0);
              return { status: 'absent' };
            }
            if (!res.ok) {
              console.error('[http.read] HTTP', res.status);
              return { status: 'failed' };
            }
            const body = await res.json();
            recordVersion(key, body.version);
            return { status: 'ok', value: body.value };
          } catch (e) {
            console.error('[http.read] feilet:', e);
            return { status: 'failed' };
          }
        }),

      get: (key: string) =>
        inOrder(key, async () => {
          try {
            const res = await apiFetch('/kv/' + encodeURIComponent(key) + NO_VALUE_OPT_IN);
            if (isAbsentAnswer(res.status)) {
              // Absent, and known to be absent: a later write can say "only if
              // it still does not exist" rather than replacing whatever appeared.
              // This also CLEARS a must-refresh — «nothing stored» (204, or 404
              // from an older API) is a fresh answer.
              recordVersion(key, 0);
              return null;
            }
            if (!res.ok) {
              // Not a fresh answer, so it must not clear a must-refresh. The
              // caller sees null either way; the token is left as it was.
              console.error('[http.get] HTTP', res.status);
              return null;
            }
            const body = await res.json();
            recordVersion(key, body.version);
            return { value: body.value as string };
          } catch (e) {
            console.error('[http.get] feilet:', e);
            return null;
          }
        }),

      set: (key: string, value: string) =>
        inOrder(key, async () => {
          // READ HERE, inside the queue — after every earlier call on this key has
          // settled — never at call time (#1297).
          const token = tokenFor(key);

          // REFUSED HERE, before any request. The last attempt told us this
          // client's copy is stale; sending anything built from it — including
          // the very document that was refused — risks overwriting whatever made
          // it stale. Only a fresh get() clears this.
          if (token?.kind === 'must-refresh') throw new StorageStaleError(key);

          // The version this client last SAW for the key, if any. Sending it
          // makes the write conditional: it lands only if nothing has changed
          // since, and otherwise fails loudly instead of erasing whatever did.
          // See the PUT handler in apps/api/src/routes/kv.ts (#245).
          const ifVersion = token?.kind === 'version' ? token.version : undefined;

          let res: Awaited<ReturnType<typeof apiFetch>>;
          try {
            res = await apiFetch('/kv/' + encodeURIComponent(key), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ifVersion === undefined ? { value } : { value, ifVersion }),
            });
          } catch (e) {
            // The request never produced an answer, so we do not know whether it
            // landed. Neither the old version nor "no precondition" is honest.
            invalidate(key);
            throw e;
          }

          if (res.status === 409) {
            // The server refused because our document is out of date. Its version
            // is reported for diagnostics and DELIBERATELY not adopted: taking it
            // would let the next set() re-send this same stale document with a
            // precondition that matches.
            const body = (await res.json().catch(() => null)) as {
              version?: number;
              error?: unknown;
              name?: unknown;
            } | null;
            invalidate(key);
            // TWO DIFFERENT REFUSALS SHARE THE STATUS, and they are told apart by
            // the CODE rather than by guessing from the shape. A version conflict
            // is retryable once the document is rebuilt; a server-owned field is
            // not retryable at all, so a caller that cannot tell them apart will
            // either loop or report the wrong thing.
            if (body?.error === 'organization_name_read_only') {
              throw new StorageReadOnlyFieldError(
                key,
                body.error,
                typeof body.name === 'string' ? body.name : null,
              );
            }
            throw new StorageConflictError(
              key,
              typeof body?.version === 'number' ? body.version : null,
            );
          }
          if (!res.ok) {
            console.error('[http.set] HTTP', res.status);
            // Ambiguous: a 5xx may or may not have applied.
            invalidate(key);
            throw new Error('HTTP lagring feilet: ' + res.status);
          }
          const body = (await res.json().catch(() => null)) as { version?: number } | null;
          // A successful write leaves this client's copy current — it is exactly
          // what was just stored — so the returned version may be trusted. Absent
          // one, recordVersion invalidates rather than guesses.
          recordVersion(key, body?.version);
          return { value };
        }),

      delete: (key: string) =>
        inOrder(key, async () => {
          // A delete moves the row too, so the token has to move with it.
          try {
            const res = await apiFetch('/kv/' + encodeURIComponent(key), { method: 'DELETE' });
            // Gone, and known to be gone: the next write is a create, which
            // `ifVersion: 0` expresses exactly. A failed delete is ambiguous —
            // the row may or may not still be there — so the copy is invalidated
            // rather than left pointing at a version that might match by luck.
            if (res.ok) recordVersion(key, 0);
            else invalidate(key);
            return { deleted: res.ok };
          } catch (e) {
            console.error('[http.delete] feilet:', e);
            invalidate(key);
            return { deleted: false };
          }
        }),

      list: async (prefix: string) => {
        try {
          const path = '/kv' + (prefix ? '?prefix=' + encodeURIComponent(prefix) : '');
          const res = await apiFetch(path);
          if (!res.ok) {
            console.error('[http.list] HTTP', res.status);
            return { keys: [] };
          }
          const body = await res.json();
          return { keys: body.keys };
        } catch (e) {
          console.error('[http.list] feilet:', e);
          return { keys: [] };
        }
      },
    };
  }

  // 2. FALLBACK — in-memory (tests, or no backend configured). Loses data on
  // refresh. The old localStorage + window.storage (Claude-artifact) backends
  // went in the Postgres migration, and the Supabase backend went with #241:
  // production is the `http` backend, and no-DB dev / tests fall through to
  // memory here.
  console.warn('[Padel24] Storage: in-memory (data overlever ikke refresh)');
  const mem = new Map<string, string>();
  return {
    backend: 'memory',
    get: async (key: string) => (mem.has(key) ? { value: mem.get(key) } : null),
    // In memory a read cannot fail: there is no request to fail.
    read: async (key: string): Promise<StorageRead> =>
      mem.has(key) ? { status: 'ok', value: mem.get(key) as string } : { status: 'absent' },
    set: async (key: string, value: string) => {
      mem.set(key, value);
      return { value };
    },
    delete: async (key: string) => {
      const had = mem.delete(key);
      return { deleted: had };
    },
    list: async (prefix: string) => ({
      keys: [...mem.keys()].filter((k) => !prefix || k.startsWith(prefix)),
    }),
  };
})();

// ============================================================
// TENANT LAYER (MSP preparation)
// Scopes all TENANT keys per tenant, so that each club/center can have its
// own isolated solution. GLOBAL keys (shared player database + shared ELO +
// engine settings) are shared across all tenants.
//
// The default tenant uses UNPREFIXED keys — i.e. completely unchanged behavior,
// no migration, zero risk to existing data. Only when a REAL tenant is
// set (setTenantId) do the keys get the prefix `tenant_<id>__`.
//
// IMPORTANT: this is PREPARATION, not security, and it is LEGACY COMPATIBILITY
// only. Real isolation is server-side: `/kv` resolves the tenant from the
// authenticated session, stores on a `(tenant_id, key)` primary key, and
// REJECTS a client-crafted `tenant_<id>__` prefix rather than stripping it.
// Client-side scoping never prevented anyone from reading another tenant's key.
// ============================================================
export const DEFAULT_TENANT = 'default';
// Global keys — shared across all tenants (shared player database, shared
// ELO, engine settings). These NEVER get a tenant prefix.
export const GLOBAL_STORAGE_KEYS = new Set([
  'players_v1', // shared player database
  'custom_players_v1', // shared: locally created players
  'elo_adjustments_v1', // shared: shared ELO corrections
  'engine_settings_v1', // shared: engine settings
]);
let _currentTenant = DEFAULT_TENANT;
export function getTenantId() {
  return _currentTenant || DEFAULT_TENANT;
}
export function setTenantId(id: string) {
  _currentTenant = id || DEFAULT_TENANT;
}
const _tenantPrefix = (t: string) => `tenant_${t}__`;
// Builds the storage key for the current tenant. Global keys and the default
// tenant are kept unprefixed; real tenants get `tenant_<id>__<key>`.
export function tenantScopeKey(key: string) {
  if (GLOBAL_STORAGE_KEYS.has(key)) return key;
  const t = getTenantId();
  if (!t || t === DEFAULT_TENANT) return key;
  return _tenantPrefix(t) + key;
}

export const Storage = {
  get backend() {
    return _RawStorage.backend;
  },
  get: (key: string) => _RawStorage.get(tenantScopeKey(key)),
  /** A read that distinguishes ABSENT from FAILED (#413). */
  read: (key: string): Promise<StorageRead> => _RawStorage.read(tenantScopeKey(key)),
  // AN EVENT DOCUMENT IS STORED WITHOUT ITS PLAYERS' E-MAIL AND PHONE (#1079).
  //
  // Here, at the one door every writer of a tournament, the series, the
  // archive, a setup or a draft goes through — the persist effects, the
  // archive, the pad actions, the setup screens — rather than at the dozen
  // places a player enters one. The engine copies the club's player object
  // whole, and nothing in a tournament reads those two fields back out of it:
  // the screens that show them resolve the player from the club's list by id.
  // The LOGICAL key decides, before the tenant prefix. Every other key, and a
  // document with nothing to remove, is written byte for byte as it was.
  //
  // Storage-first is unaffected: the caller still awaits this before setState.
  // What it keeps in memory may hold the two fields for the session; nothing
  // reads them, and the next load is the stored document.
  set: (key: string, value: string) =>
    _RawStorage.set(tenantScopeKey(key), withoutParticipantContactValue(key, value)),
  delete: (key: string) => _RawStorage.delete(tenantScopeKey(key)),
  // list scopes the prefix and strips the tenant prefix from returned keys,
  // so callers see the same (unprefixed) keys as before.
  list: async (prefix?: string) => {
    const t = getTenantId();
    const res = await _RawStorage.list(tenantScopeKey(prefix || ''));
    if (!res || !Array.isArray(res.keys)) return res;
    if (!t || t === DEFAULT_TENANT) {
      // Default tenant: hide real tenants' (prefixed) keys.
      return { ...res, keys: res.keys.filter((k: string) => !k.startsWith('tenant_')) };
    }
    const p = _tenantPrefix(t);
    return { ...res, keys: res.keys.map((k: string) => (k.startsWith(p) ? k.slice(p.length) : k)) };
  },
};
